/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component, Input, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import { type ColumnDef, type Rest,
type TableConfig, DMValidators, FormControl, FormGroup, TransferTableComponent, UNPLACEHOLDER, UtilService } from '@dm/core';
import { SelectOption } from '@dm/core/types';
import { FileSystemModeE } from 'app/common/constants';
import { ScheduleTypeE } from 'app/common/constants/hyper-cdp.const';
import { MO } from 'app/common/constants/mo';
import { type HyperCdpSchedule, type Lun } from 'app/common/types/mo';
import { SWITCH } from 'app/config';

import { ScheduleService } from '../../schedule.service';
import { ScheduleStrService } from '../../schedule-str.service';
import { type ProtectGroupAddId } from '../create-mo';
import { SpecifyStrService } from './specify-str.service';

@Component({
    selector: 'app-hyper-cdp-schedule-create-specify-obj',
    templateUrl: './specify-obj.component.html',
    styleUrls: ['./specify-obj.component.scss'],
})
export class SpecifyObjComponent implements OnInit {
    @Input() type: ScheduleTypeE = ScheduleTypeE.Block;
    @Input() vstore: SelectOption;
    @ViewChild('luntt')luntt: TransferTableComponent;
    @ViewChild('pgtt')pgtt: TransferTableComponent;
    @ViewChild('fstt')fstt: TransferTableComponent;

    vm: FormGroup;

    ScheduleTypeE = ScheduleTypeE;
    errorMessage;
    hyperCDPSchedule: HyperCdpSchedule;
    selectedPG = [];
    selectedLUN = [];
    selectedFS = [];
    lunSource: Lun[] = [];
    pgSource: ProtectGroupAddId[] = [];
    lunSourceOri: Lun[] = [];
    pgSourceOri: ProtectGroupAddId[] = [];
    pgCountRest: Rest;
    lunCountRest: Rest;
    selection = [];
    tableConfig: TableConfig;
    // columnDefs; 没用到
    LUNcolumnDefs: ColumnDef[];
    PGcolumnDefs: ColumnDef[];
    FScolumnDefs: ColumnDef[] = this.scheduleStr.filesystemColumnDefs;
    lunRest: Rest;
    protectRest: Rest;
    fsRest: Rest;
    vstoreRest: Rest;
    selectedVstore: SelectOption;
    loading = false;

    isSimpleMode = SWITCH.IS_SIMPLE_MODE;

    constructor(
        public str: SpecifyStrService,
        private scheduleStr: ScheduleStrService,
        private service: ScheduleService,
        private utilService: UtilService,
    ) {}

    ngOnInit(): void {
        this.vm = new FormGroup({
            name: new FormControl('', {
                validators: [
                    Validators.required,
                    DMValidators.rangeByteLength(1, 255),
                    DMValidators.commonName,
                ],
            }),
            vstoreItem: new FormControl(null, {
                validators: [
                    Validators.required,
                ],
            }),
            description: new FormControl('', {
                validators: [
                    DMValidators.rangeByteLength(0, 255),
                ],
            }),
        }, { updateOn: 'blur' });

        this.errorMessage = {
            name: {
                required: this.str.nullErrMsg('name'),
                rangeByteLength: this.str.lengthErrMsg(1, 255),
                commonName: this.str.nameErrMsg('name'),
            },
            description: {
                rangeByteLength: this.str.lengthErrMsg(0, 255),
            },
            vstoreItem: {
                required: this.str.nullErrMsg('vStoreLab'),
            },
        };
        this.PGcolumnDefs = [
            {
                field: 'NAME',
                header: this.str.title.name,
                filterType: 'search',
                sortable: true,
            },
            {
                field: 'ID',
                header: this.str.title.id,
            },
        ];
        this.LUNcolumnDefs = [
            {
                field: 'NAME',
                header: this.str.title.name,
                filterType: 'search',
                sortable: true,
            },
            {
                field: 'ID',
                header: this.str.title.id,
            },
            {
                field: 'CAPACITY',
                header: this.str.title.capacity,
                hidden: true,
                columnFilter: true,
                render: (value): string => {
                    if (value) {
                        return this.utilService.adaptUnit(value, 'Sector').label;
                    } else {
                        // 保护组没有容量，显示为--
                        return UNPLACEHOLDER;
                    }
                },
            },
            {
                field: 'WWN',
                header: this.str.title.wwn,
                hidden: true,
                columnFilter: true,
                render: (value): string => value || UNPLACEHOLDER,
            },
        ];

        this.tableConfig = {
            selectionMode: 'checkbox',
            dataKey: 'type_id',
            beforeShow: (data): any[] => data.map((item) => {
                item.NAME = item.protectGroupName || item.NAME;
                item.ID = item.protectGroupId || item.ID;
                item.type_id = item.TYPE + '_' + (item.protectGroupId || item.ID);
                return item;
            }),
        };

        if (this.vstore && this.vstore.value) {
            this.fsRest = this.service.getBatchFsRest(this.vstore.value);
            this.lunRest = this.service.getLun(this.vstore.value);
            this.protectRest = this.service.getProtectGroup(this.vstore.value);
        }
        this.vstoreRest = this.service.getVstoreRest();

        // 租户视图或进入不再支持选租户，文本展示
        if (this.vstore && this.vstore.value) {
            this.selectedVstore = this.vstore;
            this.vm.get('vstoreItem').disable();
        } else {
            this.vm.get('vstoreItem').enable();
        }
    }

    getSelection(): any[] {
        return this.selection;
    }

    getSelectedPG(): any[] {
        const select = this.selection;
        this.selectedPG = select.filter((item) => Number(item.TYPE) === MO.PROTECT_GROUP);
        this.selectedPG.map((item) => item.ID = Number(item.protectGroupId));
        return this.selectedPG;
    }

    getSelectedLUN(): any[] {
        const select = this.selection;
        this.selectedLUN = select.filter((item) => Number(item.TYPE) === MO.LUN);
        return this.selectedLUN;
    }

    getSelectedFS(): any[] {
        const select = this.selection;
        this.selectedFS = select.filter((item) => Number(item.TYPE) === MO.FILE_SYSTEM);
        return this.selectedFS;
    }

    selectedVstoreChange(selectOption: SelectOption): void {
        this.selection = [];
        this.selectedVstore = selectOption;

        if (this.type === ScheduleTypeE.Block) {
            this.lunRest = this.service.getLun(this.selectedVstore.value);
            this.protectRest = this.service.getProtectGroup(this.selectedVstore.value);
        } else {
            this.fsRest = this.service.getBatchFsRest(this.selectedVstore.value);
        }
    }
}
